import styled from '@emotion/styled';
import { Select } from 'antd';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import Flag from 'react-world-flags';

import { getSortedCountries } from 'src/modules/countries/selectors/countrySelectors';
import { color, font, shadow } from 'src/shared/styles/variables';

const FieldLabel = styled.p(
    {
        color: color.fiord,
        margin: '20px 0 7px !important',
    },
    font.heavy(font.size.base),
);

const StyledSelect = styled(Select)({
    width: '100%',
    marginBottom: 14,

    '.ant-select-selection--single, .ant-select-selection--multiple': {
        minHeight: 50,
        boxShadow: shadow.regularShadow,
        borderRadius: 5,
        border: `1px solid ${color.linkWaterLight}`,
    },
    '.ant-select-selection--multiple': {
        cursor: 'pointer',
    },
    '.ant-select-selection__rendered': {
        lineHeight: '48px',
        display: 'flex',
        alignItems: 'center',
    },
    '.ant-select-selection__placeholder': {
        color: color.fiord,
        ...font.heavy(font.size.base),
    },
});

const OptionWrapper = styled.div({
    display: 'flex',
    alignItems: 'center',
});

const OptionText = styled.span(
    {
        color: color.fiord,
    },
    font.heavy(font.size.base),
);

const FlagText = styled(OptionText)({
    marginBottom: -3,
    marginLeft: 14,
});

interface Props {
    country: string;
    onSelectCountry: (country: string) => void;
}

export enum CountryInputTestID {
    Select = 'CountryInput.Select',
}

export const CountryInput: React.FC<Props> = ({ country, onSelectCountry }) => {
    const { t } = useTranslation();
    const countries = useSelector(getSortedCountries);

    return (
        <>
            <FieldLabel>{t('auth:common_form_inputs.labels.country_region')}</FieldLabel>
            <StyledSelect
                data-testid={CountryInputTestID.Select}
                onChange={onSelectCountry}
                value={country}
            >
                {countries.map(({ isoCode, text, value }) => (
                    <Select.Option key={value} value={value}>
                        <OptionWrapper>
                            <Flag code={isoCode} width="36" height="24" />
                            <FlagText>{text}</FlagText>
                        </OptionWrapper>
                    </Select.Option>
                ))}
            </StyledSelect>
        </>
    );
};
